/**
 * Worthwhile Spend Check — the comparison between stated values and actual spending.
 *
 * Ported 1:1 from the marketing prototype's `tally` / `rows` / `strays` / `narrative`.
 * The ranking rule and every narrative branch below mirror that prototype exactly.
 */

import type { CategoryKey, NarrativeBlock, ResultRow, SpendCheckState, SpendItem, Stray } from '../types'
import { categoryByKey, lowerName } from './data'

const byId = (items: SpendItem[]): Record<string, SpendItem> => Object.fromEntries(items.map((it) => [it.id, it]))

type Tally = {
  /** How many of the visitor's biggest expenses fall in each pillar. */
  count: Partial<Record<CategoryKey, number>>
  /** Index of the pillar's highest-ranked expense, for tie-breaking. */
  best: Partial<Record<CategoryKey, number>>
  /** The expense labels behind each pillar. */
  labels: Partial<Record<CategoryKey, string[]>>
}

export function tally(state: SpendCheckState): Tally {
  const items = byId(state.items)
  const count: Tally['count'] = {}
  const best: Tally['best'] = {}
  const labels: Tally['labels'] = {}

  state.top.forEach((id, i) => {
    const it = items[id]
    if (!it) return
    count[it.cat] = (count[it.cat] ?? 0) + 1
    if (best[it.cat] === undefined) best[it.cat] = i
    labels[it.cat] = [...(labels[it.cat] ?? []), it.t]
  })

  return { count, best, labels }
}

/**
 * Rank each ranked pillar by spending, then pair it against its importance rank.
 *
 * Spending rank is decided by how many of the visitor's biggest expenses land in the
 * pillar, then by the highest-ranked of those expenses, then by the visitor's own
 * importance order — so a pillar never moves without a reason the visitor supplied.
 */
export function buildRows(state: SpendCheckState): ResultRow[] {
  const tl = tally(state)

  const order = [...state.imp].sort((a, b) => {
    const countA = tl.count[a] ?? 0
    const countB = tl.count[b] ?? 0
    if (countB !== countA) return countB - countA

    const bestA = tl.best[a] ?? 999
    const bestB = tl.best[b] ?? 999
    if (bestA !== bestB) return bestA - bestB

    return state.imp.indexOf(a) - state.imp.indexOf(b)
  })

  return state.imp.map((k, i) => {
    const spend = order.indexOf(k) + 1
    return { k, n: categoryByKey(k).n, imp: i + 1, spend, move: spend - (i + 1) }
  })
}

/** Pillars the visitor spends in but never ranked as a value at all. */
export function strays(state: SpendCheckState): Stray[] {
  const tl = tally(state)
  return (Object.keys(tl.count) as CategoryKey[])
    .filter((k) => !state.imp.includes(k))
    .map((k) => ({ k, n: categoryByKey(k).n, items: tl.labels[k] ?? [] }))
}

const places = (n: number) => (n === 1 ? 'one place' : `${n} places`)

/** "a", "a and b", "a, b and c" */
export const listSentence = (a: string[]): string =>
  a.length <= 1 ? (a[0] ?? '') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`

/** The observation itself, without the follow-up the on-screen copy adds. */
const strayObservation = (stray: Stray): string =>
  `You put ${listSentence(stray.items)} among your biggest expenses, but ${lowerName(stray.k)} is not on your list of values at all.`

export const strayLine = (stray: Stray): string =>
  `${strayObservation(stray)} It may be worth asking whether it belongs there, or whether that money is going somewhere you would rather it did not.`

/**
 * The biggest drop and the biggest jump, or nothing at all when every pillar moved by a
 * single place. A one-place shuffle is noise, so the prose, the chart highlight and the
 * lead's metadata all treat it as "nothing moved" rather than disagreeing with each other.
 */
export function extremes(rows: ResultRow[]): { fall?: ResultRow; rise?: ResultRow } {
  const biggest = rows.reduce((max, r) => Math.max(max, Math.abs(r.move)), 0)
  if (biggest <= 1) return {}

  let fall: ResultRow | undefined
  let rise: ResultRow | undefined
  for (const r of rows) {
    if (r.move > 0 && (!fall || r.move > fall.move)) fall = r
    if (r.move < 0 && (!rise || r.move < rise.move)) rise = r
  }
  return { fall, rise }
}

/**
 * The prose blocks under the chart: always an opening read, then — when anything moved
 * more than a single place — the biggest drop and the biggest jump.
 */
export function narrative(rows: ResultRow[]): NarrativeBlock[] {
  const holds = rows.filter((r) => r.move === 0).sort((a, b) => a.imp - b.imp)

  const drift = rows.reduce((sum, r) => sum + Math.abs(r.move), 0) / Math.max(1, rows.length)
  const wide = drift >= rows.length / 3

  const out: NarrativeBlock[] = []
  const first: NarrativeBlock = { tone: 'accent', title: 'What is already working.', paragraphs: [] }

  if (holds.length === rows.length) {
    first.paragraphs.push(
      'Both lists are in exactly the same order. Your spending tracks your priorities top to bottom. Going forward, ensure that your spending stays aligned with your values. As your income or obligations change, you may want to reassess if this order continues to work for you.'
    )
  } else if (wide) {
    first.title = 'Start here.'
    first.paragraphs.push(
      'Your two lists barely overlap. As you analyze the gaps between where you ranked your values and where you spent your money, ask which list is the more honest one. One list might describe your actual life, and one might describe the version you’d like to work toward.',
      'In both cases, the work may be the same: notice where you can cut and where you can spend more in line with your values, or acknowledge that there is another priority that is taking precedence.'
    )
  } else if (holds[0]) {
    first.paragraphs.push(
      `${categoryByKey(holds[0].k).n} sits at number ${holds[0].imp} on both lists. For this value, your spending matches your priority. As you read through the rest, look for places you could cut back to fund higher priorities, and for places that suggest a value is a higher priority for you than you originally thought.`
    )
  } else {
    first.paragraphs.push(
      'Small shifts indicate your spending is somewhat aligned with your values. While nothing held its exact position, every value moved only a place or two. That’s a different picture from two lists with little to no overlap.',
      'Keep in mind that small shifts may not mean much on their own. Instead, look for shifts that matter to you. They may mean that you could cut spending in an area to bring it in line with your priorities, or they may be a sign that a value is less of a priority than you originally thought.'
    )
  }
  out.push(first)

  const { fall: worstFall, rise: biggestRise } = extremes(rows)
  if (!worstFall && !biggestRise) return out

  if (worstFall) {
    const block: NarrativeBlock = {
      tone: 'fall',
      title: 'The widest gap',
      paragraphs: [
        `${worstFall.n} is your number ${worstFall.imp} priority and your number ${worstFall.spend} expense. It falls ${places(worstFall.move)}, the biggest drop on your list.`,
      ],
    }
    if (biggestRise) {
      block.paragraphs.push(
        `${categoryByKey(biggestRise.k).n} went the other way. Is this a spending area that could use adjustment?`
      )
    }
    out.push(block)
  }

  if (biggestRise) {
    out.push({
      tone: 'rise',
      title: 'Where the money went instead',
      paragraphs: [
        `${biggestRise.n} is your number ${biggestRise.imp} priority and your number ${biggestRise.spend} expense. It climbs ${places(-biggestRise.move)}, the biggest single jump.`,
      ],
    })
  }

  return out
}

/** The two pillars the chart highlights: the biggest drop and the biggest jump. */
export function focusKeys(rows: ResultRow[]): Set<CategoryKey> {
  const { fall, rise } = extremes(rows)
  return new Set([fall?.k, rise?.k].filter((k): k is CategoryKey => k !== undefined))
}

/**
 * Plain-text summary for the "email it to me" link and the lead's Salesforce notes.
 */
export function resultsSummary(state: SpendCheckState): string {
  const rows = buildRows(state)
  const items = byId(state.items)
  const lines: string[] = ['YOUR WORTHWHILE SPEND CHECK', '', 'Your values, in the order you ranked them:']

  state.imp.forEach((k, i) => lines.push(`  ${i + 1}. ${categoryByKey(k).n}`))
  lines.push('', 'Your biggest expenses, in order:')
  state.top.forEach((id, i) => {
    const it = items[id]
    if (it) lines.push(`  ${i + 1}. ${it.t}`)
  })

  lines.push('', 'Where your spending landed:')
  ;[...rows]
    .sort((a, b) => a.spend - b.spend)
    .forEach((r) => {
      let tag = 'held its place'
      if (r.move > 0) tag = `down ${r.move}`
      else if (r.move < 0) tag = `up ${Math.abs(r.move)}`
      lines.push(`  ${r.spend}. ${r.n} (ranked ${r.imp} by importance, ${tag})`)
    })

  narrative(rows).forEach((block) => {
    lines.push('', block.title.toUpperCase())
    block.paragraphs.forEach((p) => lines.push(p))
  })

  const stray = strays(state)
  if (stray.length) {
    lines.push('', 'SPENDING OUTSIDE YOUR LIST')
    stray.forEach((x) => lines.push(strayObservation(x)))
  }

  return lines.join('\n')
}
